/**
 * @fileoverview Event system for dragging subject nodes from the canvas
 * into generation panel subject slots.
 *
 * Uses a module-level singleton pattern (same as frameDragEvent.ts / imageDragEvent.ts).
 */

import type { SubjectCanvasNode, SubjectCategory, SubjectPose } from '@/types'

export interface SubjectDragData {
  subjectId: string
  name: string
  category: SubjectCategory
  color?: string
  mainImageUrl?: string
  mainImageAssetId?: string | null
  referenceImageUrls?: string[]
  referenceImageAssetIds?: (string | null)[]
  thumbnailUrl?: string
  voiceId?: string
  voiceLabel?: string
  /**
   * Initial cursor position (viewport-relative) at the moment the drag
   * activated. Lets a portal-rendered ghost render at the cursor on the
   * very first frame instead of waiting for the next `mousemove`.
   */
  initialClientX?: number
  initialClientY?: number
  // Character-specific fields (populated when category === 'character')
  characterSheetUrl?: string
  frontalPanelUrl?: string
  referencePanelUrls?: string[]
  portraitPanelUrl?: string
  /** V10 poses available on this subject (for set placement drop). */
  poses?: SubjectPose[]
}

export function buildSubjectDragData(
  subject: SubjectCanvasNode,
  initialPosition?: { clientX: number; clientY: number },
): SubjectDragData {
  return {
    subjectId: subject.id,
    name: subject.name,
    category: subject.category,
    color: subject.color,
    mainImageUrl: subject.mainImageUrl,
    mainImageAssetId: subject.mainImageAssetId,
    referenceImageUrls: subject.referenceImageUrls,
    referenceImageAssetIds: subject.referenceImageAssetIds,
    thumbnailUrl: subject.thumbnailUrl,
    voiceId: subject.voiceId,
    voiceLabel: subject.voiceLabel,
    characterSheetUrl: subject.characterSheetUrl,
    frontalPanelUrl: subject.frontalPanelUrl,
    referencePanelUrls: subject.referencePanelUrls,
    portraitPanelUrl: subject.portraitPanelUrl,
    poses: subject.poses,
    initialClientX: initialPosition?.clientX,
    initialClientY: initialPosition?.clientY,
  }
}

let draggedSubject: SubjectDragData | null = null
const activeStateListeners = new Set<(isActive: boolean) => void>()

function notifyActiveState(isActive: boolean): void {
  activeStateListeners.forEach(listener => listener(isActive))
}

export function setSubjectDrag(data: SubjectDragData): void {
  const wasActive = draggedSubject !== null
  draggedSubject = data
  if (!wasActive) notifyActiveState(true)
}

export function getSubjectDrag(): SubjectDragData | null {
  return draggedSubject
}

export function consumeSubjectDrag(): SubjectDragData | null {
  const data = draggedSubject
  draggedSubject = null
  if (data !== null) notifyActiveState(false)
  return data
}

export function hasSubjectDrag(): boolean {
  return draggedSubject !== null
}

export function clearSubjectDrag(): void {
  const wasActive = draggedSubject !== null
  draggedSubject = null
  if (wasActive) notifyActiveState(false)
}

/**
 * Subscribe to subject-drag active/inactive transitions.
 * Returns an unsubscribe function.
 */
export function subscribeToSubjectDragActive(listener: (isActive: boolean) => void): () => void {
  activeStateListeners.add(listener)
  return () => activeStateListeners.delete(listener)
}
